import React, { useState, useMemo } from "react";
import LegalLayout from "@/components/LegalLayout";
import { CaretDown, MagnifyingGlass } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";
import { Link } from "react-router-dom";

/**
 * FAQ — grouped by category, with a search field and per-question accordion.
 *
 * Add/edit entries in the FAQS array below. Each entry has:
 *   id           — unique slug
 *   category     — used for grouping
 *   q_da / q_en  — question in DA / EN
 *   a_da / a_en  — answer in DA / EN. Can be a string OR JSX (use <p>, <ul>, <Link>).
 */
const FAQS = [
  // ─────────────────────────────── ACCOUNT ───────────────────────────────
  {
    id: "what-is-teamfit",
    category: "general",
    q_da: "Hvad er TeamFit?",
    q_en: "What is TeamFit?",
    a_da: "TeamFit matcher atleter på niveau, tempo og mål. Opret en cykel-, løbe- eller padel-session, tegn ruten på kortet, og saml et hold på sekunder.",
    a_en: "TeamFit matches athletes by level, pace and goals. Create a cycling, running or padel session, draw the route on the map, and gather a crew in seconds.",
  },
  {
    id: "is-it-free",
    category: "general",
    q_da: "Er det gratis?",
    q_en: "Is it free?",
    a_da: "Ja, det er gratis at oprette og deltage i sessioner med op til 10 personer. Pro Crew udvider grænsen til 50 personer og giver dig ekstra synlighed og analytics.",
    a_en: "Yes — creating and joining sessions with up to 10 people is free. Pro Crew lifts the cap to 50 people and adds boosted placement and analytics.",
  },
  {
    id: "sports",
    category: "general",
    q_da: "Hvilke sportsgrene understøttes?",
    q_en: "What sports are supported?",
    a_da: "Cykling, løb og padel. Flere kommer — skriv til os hvis du savner en sportsgren.",
    a_en: "Cycling, running and padel. More may follow — email us if you'd like to see another sport.",
  },

  // ─────────────────────────────── ACCOUNT / LOGIN ───────────────────────
  {
    id: "create-account",
    category: "account",
    q_da: "Hvordan opretter jeg en konto?",
    q_en: "How do I create an account?",
    a_da: "Tryk på \"Bliv medlem\" og vælg enten e-mail/password, Apple eller Google. Det tager under 30 sekunder.",
    a_en: "Tap \"Join now\" and choose either email/password, Apple or Google. Takes under 30 seconds.",
  },
  {
    id: "forgot-password",
    category: "account",
    q_da: "Jeg har glemt mit password — hvad gør jeg?",
    q_en: "I forgot my password — what now?",
    a_da: "Tryk \"Glemt password\" på loginsiden. Brugte du Apple/Google? Så har du intet password — log ind med samme metode.",
    a_en: "Tap \"Forgot password\" on the login screen. Did you sign up with Apple/Google? Then there's no password — just sign in with the same provider.",
  },
  {
    id: "change-email",
    category: "account",
    q_da: "Kan jeg ændre min e-mail?",
    q_en: "Can I change my email?",
    a_da: "Skriv til support@teamfit.dk fra din nuværende e-mail, og vi flytter kontoen for dig.",
    a_en: "Email support@teamfit.dk from your current address and we'll move the account for you.",
  },
  {
    id: "delete-account",
    category: "account",
    q_da: "Hvordan sletter jeg min konto?",
    q_en: "How do I delete my account?",
    a_da: <>Gå til <b>Profil → Slet konto</b>. Dine personoplysninger fjernes eller anonymiseres inden for kort tid. Du kan også skrive til <a href="mailto:support@teamfit.dk">support@teamfit.dk</a>.</>,
    a_en: <>Open <b>Profile → Delete account</b>. Your personal data is removed or anonymized within a reasonable timeframe. You can also email <a href="mailto:support@teamfit.dk">support@teamfit.dk</a>.</>,
  },

  // ─────────────────────────────── SESSIONS ──────────────────────────────
  {
    id: "create-session",
    category: "sessions",
    q_da: "Hvordan opretter jeg en session?",
    q_en: "How do I create a session?",
    a_da: "Tryk på + i bunden, vælg sportsgren, sæt start og slut på kortet, vælg tidspunkt og maks. antal deltagere — done. Distancen og tempoet beregnes automatisk.",
    a_en: "Tap the + at the bottom, pick a sport, set start/end points on the map, choose a time and max participants — done. Distance and pace are calculated automatically.",
  },
  {
    id: "route-calc",
    category: "sessions",
    q_da: "Hvordan beregnes ruterne?",
    q_en: "How are routes calculated?",
    a_da: "Vi bruger OpenStreetMap + OSRM til at beregne distance, varighed og tempo for cykling og løb. Padel-sessioner viser kun start-lokation.",
    a_en: "We use OpenStreetMap + OSRM to compute distance, duration and pace for cycling and running. Padel sessions only show the start location.",
  },
  {
    id: "join-leave",
    category: "sessions",
    q_da: "Kan jeg forlade en session efter jeg har tilmeldt mig?",
    q_en: "Can I leave a session after I've joined?",
    a_da: "Ja, det kan du indtil sessionen starter. Tryk \"Forlad session\" — den næste på ventelisten rykker automatisk ind.",
    a_en: "Yes, until the session starts. Tap \"Leave session\" — the next person on the waitlist is promoted automatically.",
  },
  {
    id: "waitlist",
    category: "sessions",
    q_da: "Hvordan fungerer ventelisten?",
    q_en: "How does the waitlist work?",
    a_da: "Hvis en session er fuld, kan du komme på ventelisten. Hvis nogen forlader sessionen, rykker du automatisk op og får besked.",
    a_en: "If a session is full, you can join the waitlist. If someone leaves, you're promoted automatically and notified.",
  },
  {
    id: "rate-others",
    category: "sessions",
    q_da: "Hvordan giver jeg en bedømmelse?",
    q_en: "How do I rate other participants?",
    a_da: "Efter en session beder vi dig om at bedømme dine medløbere/cyklister. Ratings hjælper alle med at finde gode crews og fjerner ghosters.",
    a_en: "After a session we prompt you to rate your fellow runners/riders. Ratings help everyone find good crews and weed out ghosters.",
  },

  // ─────────────────────────────── PRO CREW ──────────────────────────────
  {
    id: "pro-price",
    category: "pro",
    q_da: "Hvad koster Pro Crew?",
    q_en: "What does Pro Crew cost?",
    a_da: "29 kr./md. eller 290 kr./år. Du får op til 50 deltagere pr. session, boostet placering i feedet og analytics over dine sessioner.",
    a_en: "DKK 29/month or DKK 290/year. You get up to 50 participants per session, boosted placement in the feed, and analytics for your sessions.",
  },
  {
    id: "cancel-pro",
    category: "pro",
    q_da: "Hvordan opsiger jeg mit Pro-abonnement?",
    q_en: "How do I cancel Pro?",
    a_da: <>Køb via Apple eller Google? Gå til <b>Indstillinger → Apple-ID / Google → Abonnementer → TeamFit → Annullér</b>. Du beholder Pro indtil periodens udløb.</>,
    a_en: <>Subscribed via Apple or Google? Go to <b>Settings → Apple ID / Google → Subscriptions → TeamFit → Cancel</b>. You keep Pro until the period ends.</>,
  },
  {
    id: "referrals",
    category: "pro",
    q_da: "Hvordan virker invitationer?",
    q_en: "How do referrals work?",
    a_da: "Hver bruger har en personlig kode. Når en du har inviteret opgraderer til Pro for første gang, får du automatisk +30 dages Pro gratis.",
    a_en: "Every user has a personal referral code. When someone you invite upgrades to Pro for the first time, you automatically get +30 days of Pro for free.",
  },

  // ─────────────────────────────── PRIVACY / SAFETY ──────────────────────
  {
    id: "location-safety",
    category: "privacy",
    q_da: "Er min position sikker?",
    q_en: "Is my location data safe?",
    a_da: "Vi deler kun din live-position med deltagere i den samme session, og kun mens du har valgt at dele. Vi sælger aldrig dine data.",
    a_en: "We only share your live location with participants in the same session, and only while you choose to broadcast. We never sell your data.",
  },
  {
    id: "report-user",
    category: "privacy",
    q_da: "Hvordan rapporterer jeg en bruger?",
    q_en: "How do I report a user?",
    a_da: <>Skriv til <a href="mailto:support@teamfit.dk">support@teamfit.dk</a> med navn og sessions-link. Vi læser hver henvendelse og kan suspendere konti der bryder vores <Link to="/terms">vilkår</Link>.</>,
    a_en: <>Email <a href="mailto:support@teamfit.dk">support@teamfit.dk</a> with the name and activity link. We read every report and can suspend accounts breaking our <Link to="/terms">Terms</Link>.</>,
  },

  // ─────────────────────────────── TECHNICAL ─────────────────────────────
  {
    id: "mobile-app",
    category: "tech",
    q_da: "Findes der en mobilapp?",
    q_en: "Is there a mobile app?",
    a_da: "Ja, vi arbejder på native apps til iOS og Android. Indtil videre kan du installere websiden direkte til din hjemmeskærm — den fungerer som en app (offline, fuldskærm, push).",
    a_en: "Yes, native iOS + Android apps are in the works. For now you can install the web app directly to your home screen — it runs like an app (offline, fullscreen, push).",
  },
  {
    id: "languages",
    category: "tech",
    q_da: "Hvilke sprog understøttes?",
    q_en: "What languages are supported?",
    a_da: "Dansk (standard) og engelsk. Skift sprog øverst til højre på enhver side.",
    a_en: "Danish (default) and English. Toggle the language at the top right of any page.",
  },
];

const CATEGORIES = {
  general: { da: "Generelt", en: "General" },
  account: { da: "Konto & login", en: "Account & login" },
  sessions: { da: "Sessioner", en: "Sessions" },
  pro: { da: "Pro Crew & betaling", en: "Pro Crew & billing" },
  privacy: { da: "Privatliv & sikkerhed", en: "Privacy & safety" },
  tech: { da: "Teknisk", en: "Technical" },
};

const CATEGORY_ORDER = ["general", "account", "sessions", "pro", "privacy", "tech"];

export default function FAQ() {
  const { t, lang } = useI18n();
  const [openId, setOpenId] = useState(null);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQS;
    return FAQS.filter((f) => {
      const text = `${lang === "da" ? f.q_da : f.q_en} ${
        typeof (lang === "da" ? f.a_da : f.a_en) === "string"
          ? (lang === "da" ? f.a_da : f.a_en)
          : ""
      }`.toLowerCase();
      return text.includes(q);
    });
  }, [query, lang]);

  const grouped = useMemo(() => {
    const out = {};
    for (const f of filtered) {
      (out[f.category] ||= []).push(f);
    }
    return out;
  }, [filtered]);

  return (
    <LegalLayout
      kicker={t("legal.faq_kicker")}
      title={t("legal.faq_title")}
      lastUpdated={lang === "da" ? "Maj 2026" : "May 2026"}
    >
      <p>
        {lang === "da"
          ? "Her er svarene på det vi oftest bliver spurgt om. Mangler du noget, så skriv til "
          : "Quick answers to the most common questions. If your question isn't here, email "}
        <a href="mailto:support@teamfit.dk">support@teamfit.dk</a>.
      </p>

      <div className="not-prose mt-8 mb-6 relative">
        <MagnifyingGlass
          weight="bold"
          size={16}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"
        />
        <input
          data-testid="faq-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={lang === "da" ? "Søg i FAQ…" : "Search the FAQ…"}
          className="w-full pl-11 pr-4 py-3 text-sm border focus:outline-none focus:border-[#007AFF]"
          style={{
            background: "var(--tf-surface-2)",
            borderColor: "var(--tf-border)",
            color: "var(--tf-text)",
          }}
        />
      </div>

      {filtered.length === 0 && (
        <div
          className="not-prose border border-dashed p-8 text-center text-sm text-zinc-500"
          style={{ borderColor: "var(--tf-border)" }}
          data-testid="faq-empty"
        >
          {lang === "da"
            ? "Ingen resultater. Prøv et andet ord eller skriv direkte til os."
            : "No matches. Try a different keyword or email us directly."}
        </div>
      )}

      <div className="not-prose space-y-10">
        {CATEGORY_ORDER.filter((c) => grouped[c]?.length).map((cat) => (
          <section key={cat} data-testid={`faq-cat-${cat}`}>
            <div className="label-overline text-[#007AFF] mb-3">{CATEGORIES[cat][lang]}</div>
            <div className="space-y-2">
              {grouped[cat].map((f) => {
                const open = openId === f.id;
                return (
                  <div
                    key={f.id}
                    className="border"
                    style={{ borderColor: "var(--tf-border)", background: "var(--tf-surface)" }}
                    data-testid={`faq-item-${f.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenId(open ? null : f.id)}
                      className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left hover:bg-white/[0.02]"
                      data-testid={`faq-toggle-${f.id}`}
                    >
                      <span className="font-display text-lg tracking-tight">
                        {lang === "da" ? f.q_da : f.q_en}
                      </span>
                      <CaretDown
                        weight="bold"
                        size={16}
                        className="shrink-0 transition-transform"
                        style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
                      />
                    </button>
                    {open && (
                      <div className="px-5 pb-5 text-sm" style={{ color: "var(--tf-text-2)", lineHeight: 1.7 }}>
                        {lang === "da" ? f.a_da : f.a_en}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </LegalLayout>
  );
}
